import { useState } from 'react'

import { FamilyStatus, getEligibleEmployeesWithoutFamilyStatus, saveEmployeesFamilyStatuses } from 'core'
import benefitPlanPeriods from 'core/application/adminDashboard/benefitDetails/benefitPlanPeriods'

import { Alert, Button, Col, Icon, Modal, Notification, Row, Select, SimpleSelect, Skeleton } from 'common/components'
import { useCommandRequestV2, useQueryRequestV2 } from 'common/hooks'

const W2ReportAlert = () => {
  const [showAssignFamilyStatusModal, setShowAssignFamilyStatusModal] = useState<boolean>(false)
  const [selectedEmployees, setSelectedEmployees] = useState<{ companyUserId: string; familyStatusId: string }[]>([])
  const { sendCommand: sendSelectedEmployees, isLoading } = useCommandRequestV2(({ planPeriodId, users }) =>
    saveEmployeesFamilyStatuses({ planPeriodId, assignedFamilyStatuses: users }),
  )
  const { data: allPlanPeriods } = useQueryRequestV2({
    queryFn: () => benefitPlanPeriods(),
    initialState: [],
  })
  const activePlanPeriod = allPlanPeriods.find((x) => x.active)

  const { data: employees, reload } = useQueryRequestV2({
    queryFn: () => getEligibleEmployeesWithoutFamilyStatus(activePlanPeriod.id),
    enable: !!activePlanPeriod,
  })

  const handleEmployeeStatusChange = (employeeId: string, status: string) => {
    const currentEmployees = [...selectedEmployees]
    const found = currentEmployees.find((x) => x.companyUserId === employeeId)
    if (found) {
      found.familyStatusId = status
    } else {
      currentEmployees.push({ companyUserId: employeeId, familyStatusId: status })
    }
    setSelectedEmployees([...currentEmployees])
  }

  const handleSubmit = async () => {
    const result = await sendSelectedEmployees({ planPeriodId: activePlanPeriod.id, users: selectedEmployees })
    if (result.completed) {
      Notification.open({
        type: 'success',
        message: 'Family status assigned successfully',
      })
      setShowAssignFamilyStatusModal(false)
      setSelectedEmployees([])
      reload()
    } else {
      Notification.open({
        type: 'error',
        message: 'Failed to assign family status',
      })
    }
  }

  return (
    <>
      <Alert
        showIcon
        icon={<Icon fontAwesome type="fas fa-exclamation-triangle" />}
        className={'mt-16 flex__align_center flex__justify_center'}
        type="warning"
        message={'W-2 amounts report may vary based on employees’ MEC status and family status'}
        description={
          <div>
            <div className="color-gray-8">
              Review the W-2 to see if your employees have accurately selected their coverage status for each month.
              Incorrect or missing MEC statuses may affect the tax categorization of reimbursements. Employees who have
              been invited but have not finished setting up their accounts will show zero values and will be marked with
              two asterisks (**) in the report until their family status is selected to determine allowance amounts.
            </div>
            {employees?.length > 0 && (
              <Button className="mt-8" type="default" onClick={() => setShowAssignFamilyStatusModal(true)}>
                Assign Family Status to Employees
              </Button>
            )}
          </div>
        }
      />
      <Modal
        title="Assign Family Status to Employees"
        visible={showAssignFamilyStatusModal}
        destroyOnClose
        okText="Confirm"
        closable
        okButtonProps={{ loading: isLoading, disabled: selectedEmployees.length === 0 }}
        onOk={handleSubmit}
        onCancel={() => {
          setShowAssignFamilyStatusModal(false)
          setSelectedEmployees([])
        }}
      >
        <Skeleton active loading={false}>
          <div>
            <div className="color-gray-9 font-16 pb-16">Employees below are not assigned to a family status.</div>
            <div className="color-gray-8 pb-16">
              Please select correct family status for your employees. Employees with unassigned family status will have
              their allowance amounts missing in the W2 report.
            </div>
            <Row className="pb-8">
              <Col span={12}>
                <div className="color-gray-9 text-semi-light-bold">Employee name</div>
              </Col>
              <Col span={12}>
                <div className="color-gray-9 text-semi-light-bold">Family status</div>
              </Col>
            </Row>
            {employees?.map((employee) => (
              <Row>
                <Col span={12}>
                  <div className="color-gray-9 font-16 h-32 flex flex__align_center">
                    {employee.firstName} {employee.lastName}
                  </div>
                </Col>
                <Col span={12}>
                  <div className="color-gray-9 font-16 pb-8">
                    <SimpleSelect
                      className="w-100"
                      placeholder="Select"
                      items={[
                        { id: FamilyStatus.Single, name: 'Single' },
                        { id: FamilyStatus.SingleWithDependents, name: 'Single with dependents' },
                        { id: FamilyStatus.Married, name: 'Married' },
                        {
                          id: FamilyStatus.MarriedWithDependents,
                          name: 'Married with dependents',
                        },
                      ]}
                      onChange={(value) => handleEmployeeStatusChange(employee.companyUserId, value)}
                    />
                  </div>
                </Col>
              </Row>
            ))}
          </div>
        </Skeleton>
      </Modal>
    </>
  )
}
export default W2ReportAlert
